import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  CheckCircle2, Circle, Clock, Ban, Plus, Trash2, FileText, RefreshCw,
  MessageSquare, ArrowLeft, Mail, Lightbulb, ExternalLink, Pencil, X, FlaskConical,
} from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser, getAccessToken } from '../services/auth';
import TaskCoach from '../components/TaskCoach';
import BrainChat from '../components/BrainChat';

// One board, pointed at a domain. Accounting (acct_*) and Procurement (proc_*) both
// render from here, so a fix made for one is a fix for both — the two brain pages had
// already drifted ~1300 lines apart before this was factored out, which is exactly the
// mirror-drift trap this repo keeps re-learning.
//
// The caller supplies a config: which table prefix to talk to, who the owners are, and
// the words on the page. Everything else is identical by construction.
//
// Mirrors lib/brain-db.js. Keep the two in step.
const ALL_STATUSES = [
  { key: 'not_started', label: 'Not started', icon: Circle, color: 'var(--navy)', bg: '#eef2f6' },
  { key: 'in_progress', label: 'In progress', icon: Clock, color: '#8a5a00', bg: '#fff4dd' },
  { key: 'testing', label: 'Testing', icon: FlaskConical, color: '#5b4b9e', bg: '#f0edfb' },
  { key: 'blocked', label: 'Blocked', icon: Ban, color: '#a12020', bg: '#fdecec' },
  { key: 'done', label: 'Done', icon: CheckCircle2, color: '#1c6b3f', bg: '#e6f5ec' },
];

// Which of those a board OFFERS, and what it calls them, is the board's own business.
// Every board had the same four until the Improvements board arrived wanting a fifth
// ('testing' — an item can sit in a real trial for weeks) and its own words for two of
// them (Matt Sr asked for Open / In Progress / Testing / Completed). Both are config,
// not a fork: a board that says nothing gets exactly what it has today, so Accounting,
// Procurement and Contact Center are untouched by this.
const DEFAULT_STATUS_KEYS = ['not_started', 'in_progress', 'blocked', 'done'];
// Clicking the chip cycles the WORKING states only. 'done' is deliberately not in the
// cycle: on 2026-08-18 Mercedes had three tasks completed by accident, one of them not
// even hers, because a curious click three times over landed on done and the item then
// vanished from the list, shifting the next card under her cursor. Completing is now an
// explicit, separate action. Clicking a done item's chip reopens it, which is also the
// undo she asked for.
const DEFAULT_CYCLE = ['not_started', 'in_progress', 'blocked'];

function makeVocab(cfg) {
  const keys = (cfg && cfg.statuses) || DEFAULT_STATUS_KEYS;
  const labels = (cfg && cfg.statusLabels) || {};
  const statuses = keys
    .map((k) => ALL_STATUSES.find((s) => s.key === k))
    .filter(Boolean)
    .map((s) => (labels[s.key] ? { ...s, label: labels[s.key] } : s));
  // Fall back to the full list rather than an empty one if a config ever names only
  // keys that do not exist — an empty status dropdown is a board nobody can use.
  const list = statuses.length ? statuses : ALL_STATUSES;
  const cycle = ((cfg && cfg.cycle) || DEFAULT_CYCLE).filter((k) => list.some((s) => s.key === k));
  const ring = cycle.length ? cycle : DEFAULT_CYCLE;
  return {
    statuses: list,
    statusMeta: (k) => list.find((s) => s.key === k) || ALL_STATUSES.find((s) => s.key === k) || list[0],
    nextStatus: (k) => {
      const i = ring.indexOf(k);
      return i === -1 ? ring[0] : ring[(i + 1) % ring.length];
    },
  };
}

// Context rather than props: statusMeta is read three levels down (ItemCard, EditModal)
// and threading it through every call site is how the two brain pages drifted apart in
// the first place.
const VocabCtx = React.createContext(makeVocab(null));
const useVocab = () => React.useContext(VocabCtx);
const KINDS = [
  { key: 'task', label: 'Task' },
  { key: 'phase', label: 'Phase' },
  { key: 'finding', label: 'Finding' },
  { key: 'question', label: 'Open question' },
];
const PRIORITIES = [
  { key: 1, label: 'Do first', color: 'var(--orange)' },
  { key: 2, label: 'This week', color: 'var(--navy)' },
  { key: 3, label: 'When you can', color: '#8a99a8' },
];
const prioMeta = (p) => PRIORITIES.find((x) => x.key === Number(p)) || PRIORITIES[2];

// due_date arrives as a plain 'YYYY-MM-DD' from to_char(). Parse it as a LOCAL date:
// new Date('2026-08-20') is read as UTC midnight and renders a day early in Eastern.
const parseYmd = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
const startOfToday = () => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
};
const daysOut = (ymd) => {
  const d = parseYmd(ymd);
  return d ? Math.round((d - startOfToday()) / 86400000) : null;
};
const dueMeta = (ymd, status) => {
  const n = daysOut(ymd);
  if (n === null) return null;
  const label = parseYmd(ymd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (status === 'done') return { label, color: '#8b94a3', bg: '#f2f4f7', note: '' };
  if (n < 0) return { label, color: '#a12020', bg: '#fdecec', note: `${Math.abs(n)}d overdue` };
  if (n === 0) return { label, color: '#a12020', bg: '#fdecec', note: 'today' };
  if (n <= 7) return { label, color: '#8a5a00', bg: '#fff4dd', note: `${n}d` };
  return { label, color: '#5a6272', bg: '#eef2f6', note: '' };
};

// ── Never lose what somebody typed ──────────────────────────────────────────
// Matt Sr, 2026-09-07: "When adding it keeps deleting what I'm typing after a
// few moments before actually saving it. I've tried it 3x now." He was right and
// it cost him three attempts: the newest proc_items row was a week old, so not
// one of those three ever reached the database.
//
// The form lived ONLY in the modal's own state, so ANYTHING that unmounted it
// took the typing with it and there was no way back. There is more than one way
// for that to happen here, which is why this is fixed at the state rather than at
// one trigger: the backdrop turned the whole viewport into a close button with no
// confirm (releasing a text selection past the edge of the card counts as a click
// on it), Escape did nothing at all, and neonQuery reloads the entire page on a
// 401 - so a session that lapsed while he was typing wiped the form on the next
// poll, "after a few moments", exactly as described.
//
// So the draft is written to localStorage on every keystroke and read back when
// the form reopens. A stray click, a reload, a crash or a closed laptop can no
// longer destroy it, whichever one of them it was.
const DRAFT_NS = 'cpx-brain-draft';
const draftKey = (prefix, id) => `${DRAFT_NS}:${prefix}:${id || 'new'}`;
// localStorage throws outright in private mode and on a device out of quota. A
// draft is a convenience: it must never take the form down with it.
const readDraft = (k) => {
  try { const raw = window.localStorage.getItem(k); return raw ? JSON.parse(raw) : null; }
  catch (e) { return null; }
};
const writeDraft = (k, v) => {
  try { window.localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* nothing to do */ }
};
const clearDraft = (k) => {
  try { window.localStorage.removeItem(k); } catch (e) { /* nothing to do */ }
};

const TABS = (TodayIcon) => [
  { key: 'today', label: 'Today', icon: TodayIcon },
  { key: 'roadmap', label: 'Roadmap', icon: CheckCircle2 },
  { key: 'findings', label: 'What we have learned', icon: Lightbulb },
  { key: 'docs', label: 'Documents', icon: FileText },
];

export default function BrainBoard({ config }) {
  const cfg = config;
  const vocab = useMemo(() => makeVocab(cfg), [cfg]);
  // Stable across renders so the memoised loaders below can depend on it.
  const q = useCallback((op) => `${cfg.prefix}_${op}`, [cfg.prefix]);
  const OWNERS = ['', ...cfg.owners.filter(Boolean)];
  const user = getCurrentUser();
  const roles = Array.isArray(user?.roles) ? user.roles : String(user?.roles || '').split(',').map(r => r.trim());
  const isAdmin = roles.includes('Admin');

  const [tab, setTab] = useState('today');
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [files, setFiles] = useState([]);
  const [coaching, setCoaching] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);   // item being edited, or {} for new
  const [openNotes, setOpenNotes] = useState(null); // item id whose notes are open
  const [noteDraft, setNoteDraft] = useState('');
  const [openDoc, setOpenDoc] = useState(null);
  const [toast, setToast] = useState('');
  const [showDone, setShowDone] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const d = await neonQuery(q('data'), {});
      setItems(d.items || []);
      setNotes(d.notes || []);
      setDocs(d.docs || []);
      setFiles(d.files || []);
    } catch (e) {
      setErr(e.message || 'Could not load the board.');
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };

  const save = async (patch, key) => {
    setBusy(true);
    try {
      await neonQuery(q('item_upsert'), patch);
      // Only once it is genuinely saved. Clearing on the way in would mean a
      // failed save loses the typing, which is the bug this exists to close.
      if (key) clearDraft(key);
      await load();
      flash(patch.id ? 'Saved.' : 'Added.');
      setEditing(null);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const cycleStatus = async (it) => {
    setBusy(true);
    try {
      await neonQuery(q('item_upsert'), { id: it.id, status: vocab.nextStatus(it.status) });
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // "Tell Claude" — the reporting loop. Writing a note records what you learned;
  // this is what makes sure it actually reaches Claude rather than sitting there.
  const flagForClaude = async (it, on) => {
    setBusy(true);
    try {
      await neonQuery(q('item_flag'), { id: it.id, needsClaude: on });
      await load();
      flash(on ? 'Flagged for Claude.' : 'Cleared.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const setDone = async (it, done) => {
    setBusy(true);
    try {
      await neonQuery(q('item_upsert'), { id: it.id, status: done ? 'done' : 'not_started' });
      await load();
      flash(done ? 'Marked done.' : 'Reopened.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const removeItem = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    setBusy(true);
    try { await neonQuery(q('item_delete'), { id: it.id }); await load(); flash('Deleted.'); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const addNote = async (itemId) => {
    if (!noteDraft.trim()) return;
    setBusy(true);
    try {
      await neonQuery(q('note_add'), { itemId, body: noteDraft.trim() });
      setNoteDraft(''); await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // The preview returns the whole board as HTML, so it is session-gated server-side.
  // That rules out window.open (it cannot send an Authorization header): fetch it
  // authenticated, then open the result as a blob. The tab is opened BEFORE the
  // await or the popup blocker eats it.
  const previewBrief = async () => {
    const tab = window.open('', '_blank');
    setBusy(true);
    try {
      const res = await fetch(`/.netlify/functions/${cfg.briefFn}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
        body: JSON.stringify({ dryRun: true }),
      });
      const html = await res.text();
      if (!res.ok) throw new Error(html.slice(0, 200));
      const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      if (tab) tab.location = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (tab) tab.close();
      setErr(`Could not build the preview: ${e.message}`);
    } finally { setBusy(false); }
  };

  const tasks = useMemo(() => items.filter(i => i.kind === 'task'), [items]);
  const phases = useMemo(() => items.filter(i => i.kind === 'phase'), [items]);
  const findings = useMemo(() => items.filter(i => i.kind === 'finding' || i.kind === 'question'), [items]);
  const openTasks = useMemo(() => tasks.filter(t => t.status !== 'done'), [tasks]);

  const byOwner = useMemo(() => {
    const g = {};
    for (const t of openTasks) {
      const o = t.owner || 'Unassigned';
      (g[o] = g[o] || []).push(t);
    }
    for (const k of Object.keys(g)) g[k].sort((a, b) => a.priority - b.priority || a.sort_order - b.sort_order);
    return g;
  }, [openTasks]);

  const doneTasks = useMemo(
    () => tasks.filter(t => t.status === 'done')
      .sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0)),
    [tasks]);

  const doneThisWeek = useMemo(() => {
    const since = Date.now() - 7 * 864e5;
    return tasks.filter(t => t.status === 'done' && t.completed_at && new Date(t.completed_at).getTime() >= since);
  }, [tasks]);

  const notesFor = (id) => notes.filter(n => n.item_id === id);

  return (
    <VocabCtx.Provider value={vocab}>
    <div className="page-container">
      <div className="breadcrumb">
        <a href={`#${cfg.section}`}>{cfg.sectionLabel}</a><span> / </span><span>{cfg.title}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--navy)' }}>{cfg.title}</h1>
          <p style={{ margin: '6px 0 0', color: '#5a6b7a', maxWidth: 640, lineHeight: 1.55 }}>
            {cfg.blurb}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-outline" onClick={() => setChatOpen(true)}>
            <MessageSquare size={15} style={{ marginRight: 6 }} /> Ask the brain
          </button>
          <button className="btn btn-outline" onClick={previewBrief} disabled={busy}>
            <Mail size={15} style={{ marginRight: 6 }} /> Preview the brief
          </button>
          <button className="btn btn-ghost" onClick={load} disabled={loading || busy}>
            <RefreshCw size={15} style={{ marginRight: 6 }} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => { setErr(''); setEditing({ kind: 'task', priority: 2, status: 'not_started' }); }}>
            <Plus size={15} style={{ marginRight: 6 }} /> Add
          </button>
        </div>
      </div>

      {err && (
        <div className="card" style={{ borderLeft: '4px solid #a12020', marginBottom: 14 }}>
          <div className="card-body" style={{ color: '#a12020' }}>{err}</div>
        </div>
      )}
      {toast && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, background: 'var(--navy)', color: '#fff', padding: '10px 16px', borderRadius: 4, zIndex: 60 }}>{toast}</div>
      )}

      {/* counters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 20 }}>
        {cfg.counterOwners.map(o => (
          <div key={o} className="card"><div className="card-body" style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--navy)' }}>{(byOwner[o] || []).length}</div>
            <div style={{ fontSize: 12, color: '#5a6b7a', textTransform: 'uppercase', letterSpacing: '.06em' }}>open · {o}</div>
          </div></div>
        ))}
        <div className="card"><div className="card-body" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#a12020' }}>{items.filter(i => i.status === 'blocked').length}</div>
          <div style={{ fontSize: 12, color: '#5a6b7a', textTransform: 'uppercase', letterSpacing: '.06em' }}>blocked</div>
        </div></div>
        <div className="card"><div className="card-body" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#1c6b3f' }}>{doneThisWeek.length}</div>
          <div style={{ fontSize: 12, color: '#5a6b7a', textTransform: 'uppercase', letterSpacing: '.06em' }}>done this week</div>
        </div></div>
      </div>

      {/* tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #dbe3ea', marginBottom: 18, flexWrap: 'wrap' }}>
        {[...TABS(cfg.icon), ...(cfg.extraTabs || [])].map(t => {
          const Icon = t.icon; const on = tab === t.key;
          return (
            <button key={t.key} onClick={() => { setTab(t.key); setOpenDoc(null); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px',
                fontSize: 14, fontWeight: on ? 700 : 500, color: on ? 'var(--orange)' : '#5a6b7a',
                borderBottom: on ? '2px solid var(--orange)' : '2px solid transparent', marginBottom: -1,
              }}>
              <Icon size={15} style={{ marginRight: 6, verticalAlign: -2 }} />{t.label}
            </button>
          );
        })}
      </div>

      {/* What the owner has answered that Claude has not absorbed yet. This is the
          whole reporting loop: without it, an answer written in a note never
          reaches the person who has to act on it. */}
      {!loading && items.some(i => i.needs_claude) && (
        <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--orange)', background: '#fff8f4' }}>
          <div className="card-body">
            <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>
              {items.filter(i => i.needs_claude).length} waiting for Claude
            </div>
            <div style={{ color: '#3d4b58', fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>
              Answered here but not yet acted on. Say "check the {cfg.title}" in a
              Claude session and these get read, applied, and cleared.
            </div>
            {items.filter(i => i.needs_claude).map(i => (
              <div key={i.id} style={{ fontSize: 14, padding: '2px 0' }}>
                <span style={{ color: 'var(--orange)', fontWeight: 700 }}>→</span> {i.title}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="card"><div className="card-body">Loading…</div></div>}

      {/* A board's own extra tabs (Procurement: Vendor Pricing). They load their own data,
          so they do not wait on the board's items. */}
      {(cfg.extraTabs || []).filter(t => t.key === tab).map(t => <React.Fragment key={t.key}>{t.render()}</React.Fragment>)}

      {!loading && tab === 'today' && (
        <>
          {Object.keys(byOwner).length === 0 && (
            <div className="card"><div className="card-body">Nothing open. Everything on the board is done.</div></div>
          )}
          {Object.keys(byOwner)
            .sort((a, b) => {
              const rank = (o) => {
                const i = cfg.ownerRank.indexOf(o);
                return i === -1 ? 50 : i;
              };
              return rank(a) - rank(b) || a.localeCompare(b);
            })
            .map(owner => (
              <div key={owner} style={{ marginBottom: 22 }}>
                <div style={{ fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: '#5a6b7a', marginBottom: 8 }}>
                  {owner} · {(byOwner[owner] || []).length}
                </div>
                {(byOwner[owner] || []).map(it => (
                  <ItemCard key={it.id} it={it} onCycle={cycleStatus} onEdit={setEditing} onDelete={removeItem}
                    notes={notesFor(it.id)} openNotes={openNotes} setOpenNotes={setOpenNotes}
                    noteDraft={noteDraft} setNoteDraft={setNoteDraft} addNote={addNote} busy={busy}
                    onFlag={flagForClaude} onCoach={setCoaching} onDone={setDone} />
                ))}
              </div>
            ))}
        </>
      )}

      {!loading && tab === 'today' && doneTasks.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <button onClick={() => setShowDone(v => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontSize: 12, letterSpacing: '.12em', textTransform: 'uppercase', color: '#5a6b7a' }}>
            {showDone ? '▾' : '▸'} Done ({doneTasks.length})
          </button>
          {showDone && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 13, color: '#5a6b7a', marginBottom: 10 }}>
                Marked something by mistake? Reopen puts it straight back on the list.
              </div>
              {doneTasks.map(t => (
                <div key={t.id} className="card" style={{ marginBottom: 8, opacity: .85 }}>
                  <div className="card-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 14px' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: '#5a6b7a', textDecoration: 'line-through' }}>{t.title}</div>
                      <div style={{ fontSize: 12, color: '#8b94a3' }}>
                        {t.owner || 'unassigned'}{t.completed_at ? ` · ${new Date(t.completed_at).toLocaleString()}` : ''}
                      </div>
                    </div>
                    <button className="btn btn-outline" style={{ whiteSpace: 'nowrap' }}
                      disabled={busy} onClick={() => setDone(t, false)}>Reopen</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!loading && tab === 'roadmap' && (
        <>
          {phases.map(p => {
            const kids = tasks.filter(t => t.phase === p.title.split(':')[0].trim());
            const sm = vocab.statusMeta(p.status); const SIcon = sm.icon;
            return (
              <div key={p.id} className="card" style={{ marginBottom: 14 }}>
                <div className="card-body">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <button onClick={() => cycleStatus(p)} title="Click to change status"
                      style={{ background: sm.bg, color: sm.color, border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>
                      <SIcon size={12} style={{ verticalAlign: -2, marginRight: 5 }} />{sm.label}
                    </button>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--navy)' }}>{p.title}</div>
                      {p.detail && <div style={{ color: '#5a6b7a', marginTop: 5, lineHeight: 1.55 }}>{p.detail}</div>}
                      {kids.length > 0 && (
                        <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: '2px solid #dbe3ea' }}>
                          {kids.map(k => {
                            const km = vocab.statusMeta(k.status);
                            return (
                              <div key={k.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0', fontSize: 14 }}>
                                <span style={{ color: km.color, fontSize: 11, fontWeight: 700, minWidth: 84, textTransform: 'uppercase' }}>{km.label}</span>
                                <span style={{ color: k.status === 'done' ? '#8b94a3' : 'var(--ink, #1f2d3a)', textDecoration: k.status === 'done' ? 'line-through' : 'none' }}>{k.title}</span>
                                {k.owner && <span style={{ color: '#8b94a3', fontSize: 12 }}>({k.owner})</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <button className="btn btn-ghost" onClick={() => setEditing(p)} title="Edit"><Pencil size={14} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {!loading && tab === 'findings' && (
        <>
          <p style={{ color: '#5a6b7a', marginTop: 0, maxWidth: 680, lineHeight: 1.6 }}>
            Things we worked out that would otherwise have to be rediscovered. These shape how
            the checks get built, so they are worth reading before changing anything.
          </p>
          {findings.map(f => (
            <div key={f.id} className="card" style={{ marginBottom: 12, borderLeft: '3px solid var(--orange)' }}>
              <div className="card-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>{f.title}</div>
                  <button className="btn btn-ghost" onClick={() => setEditing(f)}><Pencil size={14} /></button>
                </div>
                {f.detail && <div style={{ color: '#3d4b58', marginTop: 6, lineHeight: 1.6 }}>{f.detail}</div>}
              </div>
            </div>
          ))}
          {!findings.length && <div className="card"><div className="card-body">Nothing recorded yet.</div></div>}
        </>
      )}

      {!loading && tab === 'docs' && (
        openDoc ? <DocReader path={openDoc} onBack={() => setOpenDoc(null)} />
          : (
            <>
              {docs.length === 0 && (
                <div className="card"><div className="card-body" style={{ color: '#5a6b7a' }}>
                  {cfg.docsEmpty}
                </div></div>
              )}
              {docs.map(d => (
                <div key={d.path} className="card" style={{ marginBottom: 10, cursor: 'pointer' }} onClick={() => setOpenDoc(d.path)}>
                  <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{d.title || d.path}</div>
                      <div style={{ fontSize: 12, color: '#8b94a3' }}>{d.category} · {Math.round((d.bytes || 0) / 1024)} KB</div>
                    </div>
                    <ExternalLink size={16} color="#8b94a3" />
                  </div>
                </div>
              ))}
            </>
          )
      )}

      {chatOpen && (
        <BrainChat config={cfg} onChanged={load} onClose={() => setChatOpen(false)} />
      )}
      {coaching && (
        <TaskCoach item={coaching} files={files} notes={notesFor(coaching.id)} config={cfg}
          onClose={() => setCoaching(null)} onChanged={load} />
      )}

      {editing && (
        <EditModal item={editing} onClose={() => setEditing(null)} onSave={save} busy={busy}
          isAdmin={isAdmin} owners={OWNERS} prefix={cfg.prefix} error={err} />
      )}
    </div>
    </VocabCtx.Provider>
  );
}

function ItemCard({ it, onCycle, onEdit, onDelete, notes, openNotes, setOpenNotes, noteDraft, setNoteDraft, addNote, busy, onFlag, onCoach, onDone }) {
  const vocab = useVocab();
  const sm = vocab.statusMeta(it.status); const SIcon = sm.icon;
  const pm = prioMeta(it.priority);
  const dm = dueMeta(it.due_date, it.status);
  const notesOpen = openNotes === it.id;

  return (
    <div className="card" style={{ marginBottom: 10, borderLeft: `4px solid ${pm.color}` }}>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <button onClick={() => onCycle(it)} disabled={busy} title="Click to change status"
            style={{ background: sm.bg, color: sm.color, border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>
            <SIcon size={12} style={{ verticalAlign: -2, marginRight: 5 }} />{sm.label}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: pm.color, textTransform: 'uppercase', letterSpacing: '.05em' }}>{pm.label}</span>
              {it.phase && <span style={{ fontSize: 11, color: '#8b94a3' }}>{it.phase}</span>}
              {dm && (
                <span style={{ fontSize: 11, background: dm.bg, color: dm.color, padding: '1px 7px', borderRadius: 3, fontWeight: 600 }}>
                  {dm.label}{dm.note ? ` · ${dm.note}` : ''}
                </span>
              )}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginTop: 4, lineHeight: 1.35 }}>{it.title}</div>
            {it.detail && <div style={{ color: '#3d4b58', marginTop: 5, lineHeight: 1.6 }}>{it.detail}</div>}
            {it.link && (
              <a href={it.link} target="_blank" rel="noreferrer" style={{ color: 'var(--orange)', fontSize: 13, display: 'inline-block', marginTop: 6 }}>
                Open link <ExternalLink size={12} style={{ verticalAlign: -1 }} />
              </a>
            )}

            <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
              {onDone && (
                <button className="btn btn-ghost" style={{ padding: '3px 10px', fontSize: 12, color: '#1c6b3f', border: '1px solid #cfe3d7' }}
                  disabled={busy} onClick={() => onDone(it, true)}>
                  Mark done
                </button>
              )}
              {onCoach && it.status !== 'done' && (
                <button className="btn btn-primary" style={{ padding: '3px 12px', fontSize: 12 }}
                  onClick={() => onCoach(it)}>
                  Walk me through this
                </button>
              )}
              <button className="btn btn-ghost" style={{ padding: '2px 8px', fontSize: 12 }}
                onClick={() => { setOpenNotes(notesOpen ? null : it.id); setNoteDraft(''); }}>
                <MessageSquare size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
                {notes.length ? `${notes.length} note${notes.length === 1 ? '' : 's'}` : 'Add a note'}
              </button>
              {onFlag && (
                <button className="btn btn-ghost" disabled={busy}
                  onClick={() => onFlag(it, !it.needs_claude)}
                  title="Write what you found in a note, then flag it so Claude picks it up"
                  style={{
                    padding: '2px 8px', fontSize: 12,
                    color: it.needs_claude ? 'var(--orange)' : '#8b94a3',
                    fontWeight: it.needs_claude ? 700 : 500,
                  }}>
                  {it.needs_claude ? '● Flagged for Claude' : 'Tell Claude'}
                </button>
              )}
            </div>

            {notesOpen && (
              <div style={{ marginTop: 10, background: '#f6f8fa', padding: 12, borderRadius: 4 }}>
                {notes.map(n => (
                  <div key={n.id} style={{ marginBottom: 8, fontSize: 13, lineHeight: 1.55 }}>
                    <span style={{ color: '#8b94a3' }}>{n.author} · {new Date(n.created_at).toLocaleDateString()}</span>
                    <div>{n.body}</div>
                  </div>
                ))}
                <textarea className="form-textarea" rows={2} value={noteDraft} placeholder="What happened, what you found, what you decided…"
                  onChange={e => setNoteDraft(e.target.value)} style={{ width: '100%', marginTop: 4 }} />
                <button className="btn btn-primary" style={{ marginTop: 6 }} disabled={busy || !noteDraft.trim()}
                  onClick={() => addNote(it.id)}>Save note</button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button className="btn btn-ghost" onClick={() => onEdit(it)} title="Edit"><Pencil size={14} /></button>
            <button className="btn btn-ghost" onClick={() => onDelete(it)} title="Delete"><Trash2 size={14} color="#a12020" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditModal({ item, onClose, onSave, busy, owners, prefix, error }) {
  const vocab = useVocab();
  const key = draftKey(prefix, item.id);
  const seed = useRef({
    id: item.id, kind: item.kind || 'task', phase: item.phase || '', title: item.title || '',
    detail: item.detail || '', status: item.status || 'not_started', owner: item.owner || '',
    link: item.link || '', dueDate: item.due_date || '', priority: item.priority || 2,
  });
  // Read the draft ONCE, on mount. Reading it on every render would let a stale
  // copy land back under somebody's cursor mid-sentence.
  const [f, setF] = useState(() => {
    const d = readDraft(key);
    return d ? { ...seed.current, ...d, id: item.id } : seed.current;
  });
  const [restored, setRestored] = useState(() => !!readDraft(key));
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const same = (a, b) => String(a === null || a === undefined ? '' : a) === String(b === null || b === undefined ? '' : b);
  const dirty = Object.keys(seed.current).some(k => !same(f[k], seed.current[k]));

  // Every keystroke, so there is no window in which the typing exists only in
  // memory. Untouched writes nothing, or opening an item and closing it again
  // would leave a phantom draft to be "restored" next time.
  useEffect(() => { if (dirty) writeDraft(key, f); }, [f, dirty, key]);

  const startOver = () => { clearDraft(key); setF(seed.current); setRestored(false); };

  const requestClose = useCallback(() => {
    if (dirty && !window.confirm('Close without saving? What you typed is kept, and it comes back the next time you open this.')) return;
    onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    // Catches the page reload api.js does on a 401, and an accidental browser
    // refresh, while there is unsaved text on the screen.
    const onLeave = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('keydown', onKey);
    window.addEventListener('beforeunload', onLeave);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('beforeunload', onLeave); };
  }, [requestClose, dirty]);

  // A click only counts as "outside" when it BEGAN outside. Selecting text in the
  // title and releasing past the edge of the card fires a click on the backdrop,
  // and that alone used to throw the whole form away.
  const downOutside = useRef(false);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,20,40,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 80, padding: 16 }}
      onMouseDown={e => { downOutside.current = e.target === e.currentTarget; }}
      onClick={e => { if (e.target === e.currentTarget && downOutside.current) requestClose(); downOutside.current = false; }}>
      <div className="card" style={{ maxWidth: 620, width: '100%', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{item.id ? 'Edit item' : 'Add an item'}</span>
          <button className="btn btn-ghost" onClick={requestClose}><X size={16} /></button>
        </div>
        <div className="card-body">
          {restored && (
            <div style={{ background: '#fff8f4', border: '1px solid var(--orange)', borderRadius: 4, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#3d4b58' }}>
              Picked up where you left off.{' '}
              <button className="btn btn-ghost" style={{ padding: 0, fontSize: 13, color: 'var(--orange)', textDecoration: 'underline' }} onClick={startOver}>
                Start over
              </button>
            </div>
          )}
          {error && (
            <div style={{ background: '#fdecec', border: '1px solid #a12020', borderRadius: 4, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#a12020' }}>
              {error} Nothing was lost, so Save again when you are ready.
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Title</label>
            <input className="form-input" value={f.title} onChange={e => set('title', e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Detail</label>
            <textarea className="form-textarea" rows={4} value={f.detail} onChange={e => set('detail', e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" value={f.kind} onChange={e => set('kind', e.target.value)}>
                {KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Owner</label>
              <select className="form-select" value={f.owner} onChange={e => set('owner', e.target.value)}>
                {owners.map(o => <option key={o} value={o}>{o || 'Unassigned'}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={f.status} onChange={e => set('status', e.target.value)}>
                {vocab.statuses.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-select" value={f.priority} onChange={e => set('priority', Number(e.target.value))}>
                {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Phase</label>
              <input className="form-input" value={f.phase} placeholder="Phase 2" onChange={e => set('phase', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Due date</label>
              <input className="form-input" type="date" value={f.dueDate || ''} onChange={e => set('dueDate', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Link (optional)</label>
            <input className="form-input" value={f.link} onChange={e => set('link', e.target.value)} placeholder="https://" />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn btn-outline" onClick={requestClose}>Cancel</button>
            <button className="btn btn-primary" disabled={busy || !f.title.trim()}
              onClick={() => onSave({ ...f, dueDate: f.dueDate || null }, key)}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocReader({ path, prefix, onBack }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    neonQuery(`${prefix}_doc_get`, { path })
      .then(d => { if (live) setDoc(d); })
      .catch(e => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [path, prefix]);

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="btn btn-ghost" onClick={onBack}><ArrowLeft size={15} /> Back</button>
        <span>{doc?.title || path}</span>
      </div>
      <div className="card-body">
        {err && <div style={{ color: '#a12020' }}>{err}</div>}
        {!doc && !err && 'Loading…'}
        {doc && (
          <div className="markdown-body" style={{ lineHeight: 1.65 }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.body || ''}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
